import React from "react";
import { Box, Typography, Chip, Button } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import MunicipalityTable from "../MunicipalityDasboard/MunicipalityTable";
import DarkDateRangePicker from "../OperatorDashboard/DarkDateRangePicker";
import {
  useGetMVTableDataQuery,
  useGetMuniReportViewQuery,
} from "../MunicipalityDasboard/municipalityDataAPISlice";
import { useGetFieldAreaListQuery } from "../api/apiSlice";
import DownloadIcon from "@mui/icons-material/Download";

const darkCard = { backgroundColor: "#161B22", border: "1px solid rgba(255,255,255,0.06)" };

const TOC_ITEMS = [
  { label: "Summary",               id: "summary" },
  { label: "Peak Shaved",           id: "peak-shaved" },
  { label: "Target Maximum Demand", id: "target-max" },
  { label: "ADD",                  id: "admd" },
  { label: "Average Power (MVA)",   id: "avg-power" },
  { label: "Installation",          id: "installation" },
  { label: "Notes & findings",      id: "notes" },
];

const fmt = (n, decimals = 2) =>
  Number(n).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

/* Reusable row: left label card + right monthly data cells */
function SectionRow({ id, title, subtitle, extra, data, unit }) {
  const entries = data ? Object.entries(data) : [];
  return (
    <Box
      id={id}
      sx={{
        ...darkCard,
        borderRadius: "12px",
        display: "flex",
        overflow: "hidden",
        mb: 2,
        scrollMarginTop: "108px",
      }}
    >
      {/* Left label */}
      <Box
        sx={{
          width: 200,
          minWidth: 200,
          p: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          borderRight: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <Typography sx={{ color: "#e0e6f0", fontWeight: 700, fontSize: "1rem", mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem" }}>
            {subtitle}
          </Typography>
        )}
        {extra}
      </Box>

      {/* Right month cells */}
      <Box sx={{ flex: 1, display: "flex" }}>
        {entries.map(([month, value], i) => (
          <Box
            key={month}
            sx={{
              flex: 1,
              p: 3,
              borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.06)" : "none",
            }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.38)",
                fontSize: "0.68rem",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              {month}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "5px" }}>
              <Typography sx={{ color: "#e0e6f0", fontWeight: 700, fontSize: "1.6rem" }}>
                {fmt(value)}
              </Typography>
              {unit && (
                <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.82rem" }}>
                  {unit}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const TechnicalReportPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = React.useState("summary");
  const [countStockField, setCountStockField] = React.useState(0);
  const [fieldAreas, setFieldAreas] = React.useState(0);

  const { data: areas, isSuccess: isGetAreasSuccess } = useGetFieldAreaListQuery();

  React.useEffect(() => {
    if (isGetAreasSuccess && areas.field_areas.length > 0) {
      const totalStockCount = areas.field_areas.reduce(
        (acc, item) => acc + item.stock_item_count,
        0
      );
      setCountStockField(totalStockCount);
      setFieldAreas(areas.field_areas.length);
    }
  }, [isGetAreasSuccess, areas, countStockField]);

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [startDate, setstartDate] = React.useState(dayjs().subtract(30, "day"));
  const [endDate, setendDate] = React.useState(dayjs());

  const request = {
    startDate: startDate.format("YYYY-MM-DD"),
    endDate: endDate.format("YYYY-MM-DD"),
    user_timezone: userTimezone,
  };

  const {
    data: municipalityTableData,
    isLoading: isLoadingMunicipalityTableData,
    isSuccess: isSuccessMunicipalityTableData,
    isError: isErrorMunicipalityTableData,
    error: errorMessageMunicipalityTableData,
  } = useGetMVTableDataQuery(request);

  const { data: muniReportData, isSuccess: isSuccessmuniReportData } =
    useGetMuniReportViewQuery(request);

  const handleDownloadCSV = () => {
    let csvContent = "Category,Month,Value\n";

    const addKeyValuePairsToCSV = (category, dataObj) => {
      Object.entries(dataObj).forEach(([month, value]) => {
        csvContent += `${category},${month},${value}\n`;
      });
    };

    if (muniReportData) {
      if (muniReportData.demand_shaved)
        addKeyValuePairsToCSV("Demand Shaved", muniReportData.demand_shaved);
      if (muniReportData.target_max)
        addKeyValuePairsToCSV("Target Maximum", muniReportData.target_max);
      if (muniReportData.admd)
        addKeyValuePairsToCSV("ADD", muniReportData.admd);
      if (muniReportData.energy_with_intervention?.length > 0)
        csvContent += `Energy With Intervention,,${muniReportData.energy_with_intervention[0]}\n`;
      if (muniReportData.energy_shifted?.length > 0)
        csvContent += `Energy Shifted,,${muniReportData.energy_shifted[0]}\n`;
      csvContent += `Count Stock Field,,${countStockField}\n`;
      csvContent += `Field Areas,,${fieldAreas}\n`;
    }

    if (csvContent !== "Category,Month,Value\n") {
      const a = document.createElement("a");
      a.href = `data:text/csv;charset=utf-8,${encodeURI(csvContent)}`;
      a.target = "_blank";
      a.download = `MuniReport_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setActiveSection(id);
  };

  if (!isSuccessmuniReportData) return <></>;

  /* Metadata summary line */
  const peakShaved =
    muniReportData?.demand_shaved
      ? Math.max(...Object.values(muniReportData.demand_shaved))
      : null;

  return (
    <Box
      sx={{
        display: "flex",
        maxWidth: 1500,
        margin: "0 auto",
        padding: "72px 32px 48px",
        minHeight: "100vh",
        color: "#e0e6f0",
        gap: "24px",
      }}
    >
      {/* ── Sidebar ── */}
      <Box sx={{ width: 220, flexShrink: 0, mt: "24px" }}>
        <Box
          sx={{
            position: "sticky",
            top: "80px",
            ...darkCard,
            borderRadius: "12px",
            p: "12px 8px",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              color: "rgba(255,255,255,0.28)",
              mb: 1.5,
              pl: "10px",
            }}
          >
            CONTENTS
          </Typography>
          {TOC_ITEMS.map(({ label, id }) => (
            <Box
              key={id}
              onClick={() => scrollTo(id)}
              sx={{
                px: "10px",
                py: "7px",
                borderRadius: "6px",
                cursor: "pointer",
                borderLeft: activeSection === id
                  ? "2px solid #FDB940"
                  : "2px solid transparent",
                backgroundColor: activeSection === id
                  ? "rgba(253,185,64,0.08)"
                  : "transparent",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.83rem",
                  color: activeSection === id ? "#FDB940" : "rgba(255,255,255,0.6)",
                  fontWeight: activeSection === id ? 600 : 400,
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>

        {/* Header card */}
        <Box id="summary" sx={{ ...darkCard, borderRadius: "12px", p: 3, mb: 3, scrollMarginTop: "108px" }}>
          <Typography
            sx={{ fontSize: "1.8rem", fontWeight: 800, color: "#fff", letterSpacing: "-0.03em", mb: 1 }}
          >
            Technical Report
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
            <Chip
              label="City of Tshwane"
              size="small"
              sx={{
                backgroundColor: "rgba(253,185,64,0.15)",
                color: "#FDB940",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 24,
              }}
            />
            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.82rem" }}>
              {peakShaved !== null && `· Peak shaved ${fmt(peakShaved)} MVA`}
              {countStockField > 0 && ` · ${countStockField.toLocaleString()} devices`}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <DarkDateRangePicker
              compact
              startDate={startDate}
              endDate={endDate}
              onApply={(start, end) => { setstartDate(start); setendDate(end); }}
            />
            <Button
              onClick={handleDownloadCSV}
              variant="contained"
              startIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
              sx={{
                backgroundColor: "#FDB940",
                color: "#0d1117",
                fontWeight: 700,
                fontSize: "0.85rem",
                textTransform: "none",
                px: 2,
                py: 0.9,
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#e6a830", boxShadow: "none" },
              }}
            >
              Download Report
            </Button>
          </Box>
        </Box>

        {/* Stat cards */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          {[
            { label: "Period Consumption Total", value: muniReportData?.energy_with_intervention },
            { label: "With Intervention",        value: muniReportData?.energy_with_intervention },
            { label: "Total Load Shifted",       value: muniReportData?.energy_shifted },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{
                flex: 1,
                ...darkCard,
                borderRadius: "12px",
                p: 3,
              }}
            >
              <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.82rem", mb: 1 }}>
                {label}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                <Typography sx={{ color: "#FDB940", fontWeight: 800, fontSize: "2rem", letterSpacing: "-0.02em" }}>
                  {fmt(Array.isArray(value) ? value[0] : value)}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.88rem" }}>
                  MWh
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Peak Shaved */}
        <SectionRow
          id="peak-shaved"
          title="Peak Shaved"
          subtitle="Monthly demand reduction (MVA)"
          data={muniReportData?.demand_shaved}
          unit="MVA"
        />

        {/* Target Maximum Demand */}
        <SectionRow
          id="target-max"
          title="Target Maximum Demand"
          subtitle="Monthly target (MVA)"
          data={muniReportData?.target_max}
          unit="MVA"
        />

        {/* ADMD */}
        <SectionRow
          id="admd"
          title="ADD"
          subtitle="Monthly average ADD"
          extra={
            <Typography
              onClick={() => navigate("/municipality/admd/")}
              sx={{
                color: "#FDB940",
                fontSize: "0.82rem",
                mt: 1.5,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View ADD chart →
            </Typography>
          }
          data={muniReportData?.admd}
          unit={null}
        />

        {/* Average Power (MVA) */}
        <Box
          id="avg-power"
          sx={{ ...darkCard, borderRadius: "12px", overflow: "hidden", mb: 2, scrollMarginTop: "108px" }}
        >
          <MunicipalityTable
            municipalityTableData={municipalityTableData}
            isLoadingMunicipalityTableData={isLoadingMunicipalityTableData}
            isSuccessMunicipalityTableData={isSuccessMunicipalityTableData}
            isErrorMunicipalityTableData={isErrorMunicipalityTableData}
            errorMessageMunicipalityTableData={errorMessageMunicipalityTableData}
            startDate={startDate}
            endDate={endDate}
          />
        </Box>

        {/* Installation Information */}
        <Box
          id="installation"
          sx={{
            ...darkCard,
            borderRadius: "12px",
            display: "flex",
            overflow: "hidden",
            mb: 2,
            scrollMarginTop: "108px",
          }}
        >
          {/* Left label */}
          <Box
            sx={{
              width: 200,
              minWidth: 200,
              p: 3,
              borderRight: "1px solid rgba(255,255,255,0.06)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
          >
            <Typography sx={{ color: "#e0e6f0", fontWeight: 700, fontSize: "1rem", mb: 0.5 }}>
              Installation Information
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.78rem" }}>
              Roll-out coverage at end of reporting period
            </Typography>
          </Box>

          {/* Right stat cells */}
          <Box sx={{ flex: 1, display: "flex" }}>
            {[
              { label: "Total installations", value: countStockField },
              { label: "Field areas covered", value: fieldAreas },
            ].map(({ label, value }, i) => (
              <Box
                key={label}
                sx={{
                  flex: 1,
                  p: 3,
                  borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.06)" : "none",
                }}
              >
                <Typography sx={{ color: "rgba(255,255,255,0.38)", fontSize: "0.78rem", mb: 1 }}>
                  {label}
                </Typography>
                <Typography sx={{ color: "#e0e6f0", fontWeight: 700, fontSize: "2rem" }}>
                  {Number(value).toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Notes & findings placeholder */}
        <Box
          id="notes"
          sx={{ ...darkCard, borderRadius: "12px", p: 3, scrollMarginTop: "108px" }}
        >
          <Typography sx={{ color: "#e0e6f0", fontWeight: 700, fontSize: "1rem", mb: 0.5 }}>
            Notes &amp; Findings
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.85rem" }}>
            No notes recorded for this period.
          </Typography>
        </Box>

      </Box>
    </Box>
  );
};

export default TechnicalReportPage;
